export type ExerciseTemplateKind = 'normal' | 'warmup' | 'dropset' | 'failure'

export const EXERCISE_TEMPLATE_KINDS: ExerciseTemplateKind[] = [
  'normal',
  'warmup',
  'dropset',
  'failure',
]

/**
 * How a block defines its work. The server does not store this: it derives
 * `manual` from which of the two fields arrives (reps_target ⇒ manual,
 * work_seconds ⇒ timed), and rejects a payload carrying both or neither. The
 * editor keeps it explicit so the form can only ever send one.
 */
export type ExerciseTemplateMode = 'timed' | 'manual'

/**
 * What happens after a block, before the next one starts: roll straight on,
 * count a rest down, or wait for the athlete to tap start. `manual` is what the
 * timer did between every pair of blocks before this was configurable, so it
 * stays the default. Unused on the last block — nothing follows it.
 */
export type ExerciseTemplateTransition = 'none' | 'timed' | 'manual'

export const EXERCISE_TEMPLATE_TRANSITIONS: ExerciseTemplateTransition[] = ['none', 'timed', 'manual']

/**
 * A block being written in the editor, before it is sent to the server. Every
 * number is nullable here and nowhere else: a half-typed field is empty, not
 * zero, and turning that into a 0 while someone is still typing would fight
 * them for the cursor. Validation is what rejects the ones that must be filled.
 */
export interface ExerciseTemplateInput {
  /** Stable local key for React and for drag-and-drop, independent of the id. */
  key: string
  /** Server id. A block is created the moment it is added, so this is always set. */
  id: string
  title: string
  short_description: string
  kind: ExerciseTemplateKind
  mode: ExerciseTemplateMode
  sets: number | null
  reps_per_set: number | null
  /** Timed mode: seconds of work per rep. */
  work_seconds: number | null
  /** Manual mode: reps to hit in each rep block. */
  reps_target: number | null
  rest_seconds: number | null
  set_rest_seconds: number | null
  weight_percent: number | null
  rpe: number | null
  alternate_side: boolean
  side_change_seconds: number | null
  transition_kind: ExerciseTemplateTransition
  /** Seconds of pause before the next block. Only set when the kind is timed. */
  transition_seconds: number | null
}

/** What the API accepts for one block — the shape the editor input maps onto. */
export interface ExerciseTemplatePayload {
  title: string
  short_description?: string | null
  kind: ExerciseTemplateKind
  sets: number
  reps_per_set: number
  work_seconds?: number
  reps_target?: number
  rest_seconds: number
  set_rest_seconds: number
  weight_percent?: number | null
  rpe?: number | null
  alternate_side: boolean
  side_change_seconds?: number | null
  transition_kind: ExerciseTemplateTransition
  transition_seconds?: number
}

export interface ExerciseTemplate {
  id: string
  exercise_id: string
  position: number
  kind: ExerciseTemplateKind
  manual: boolean
  title: string | null
  short_description: string | null
  sets: number
  reps_per_set: number
  work_seconds: number | null
  reps_target: number | null
  rest_seconds: number
  set_rest_seconds: number
  weight_percent: number | null
  rpe: number | null
  alternate_side: boolean
  side_change_seconds: number | null
  transition_kind: ExerciseTemplateTransition
  /** Seconds of pause before the next block. Only set when the kind is timed. */
  transition_seconds: number | null
}
